import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  LinearProgress,
  TextField,
  Typography
} from '@material-ui/core';
import { Alert, AlertTitle } from '@material-ui/lab';
import { State, WorkflowInvoke } from '@sierralabs/workflow';

import JiraIssueTypePicker from '../../../jira-connect/pickers/JiraIssueTypePicker';
import JiraProjectPicker from '../../../jira-connect/pickers/JiraProjectPicker';
import {
  useCreateJiraIssue,
  useFindJiraIssues,
  useJiraConnectAPI
} from '../../../jira-connect/JiraConnect.hooks';
import {
  JiraIssue,
  JiraIssueCreateResponse
} from '../../../jira-connect/JiraConnect.interface';
import {
  CreateJiraIssueParams,
  TaskContext
} from '../../../tasks/Task.machine';
import { useCreateTaskJiraIssue } from '../../../tasks/TaskJira.hooks';
import {
  ServiceConfiguratorProps,
  ServiceDisplayProps,
  ServicePlugin
} from '../ServicePlugin';

const StyledConfiguratorDiv = styled.div`
  .field {
    margin-top: 12px;
    margin-bottom: 12px;
  }
  .MuiFormControlLabel-root {
    width: 100%;
  }
`;

const CreateJiraIssueConfigurator: React.FC<
  ServiceConfiguratorProps
> = props => {
  const { onChange } = props;
  const [invoke, setInvoke] = useState(props.invoke);
  if (!invoke.data) invoke.data = {};
  const data: CreateJiraIssueParams['data'] = invoke.data;
  return (
    <StyledConfiguratorDiv>
      <JiraProjectPicker
        value={data.projectId}
        textFieldProps={{ size: 'small' }}
        onChange={projectId => {
          data.projectId = projectId;
          data.issueTypeId = undefined;
          const updatedInvoke = { ...invoke, data };
          setInvoke(updatedInvoke);
          onChange?.(updatedInvoke);
        }}
      />
      <JiraIssueTypePicker
        value={data.issueTypeId}
        projectId={data.projectId}
        disabled={!data.projectId}
        textFieldProps={{ size: 'small' }}
        onChange={issueType => {
          data.issueTypeId = issueType?.id;
          const updatedInvoke = { ...invoke, data };
          setInvoke(updatedInvoke);
          onChange?.(updatedInvoke);
        }}
      />
      <FormControlLabel
        label='Enable title template'
        control={
          <Checkbox
            color='primary'
            checked={data.enableTitleTemplate || false}
            onChange={(_, checked) => {
              if (!checked) {
                data.titleTemplate = undefined;
              }
              data.enableTitleTemplate = checked;
              const updatedInvoke = { ...invoke, data };
              setInvoke(updatedInvoke);
              onChange?.(updatedInvoke);
            }}
          />
        }
      />
      {data.enableTitleTemplate && (
        <>
          <Alert severity='info'>
            This allows a custom title that includes the task <b>key</b> and{' '}
            <b>name</b>.
          </Alert>
          <TextField
            className='field'
            variant='outlined'
            label='Title Template'
            size='small'
            helperText='e.g. [Automated Task] {key}: {name}'
            fullWidth
            value={data.titleTemplate || ''}
            onChange={event => {
              data.titleTemplate = event.target.value;
              const updatedInvoke = { ...invoke, data };
              setInvoke(updatedInvoke);
              onChange?.(updatedInvoke);
            }}
          />
        </>
      )}
      <FormControlLabel
        label='Link to Current QMS Entity'
        control={
          <Checkbox
            color='primary'
            checked={data.linkToEntity || false}
            onChange={(_, checked) => {
              data.linkToEntity = checked;
              const updatedInvoke = { ...invoke, data };
              setInvoke(updatedInvoke);
              onChange?.(updatedInvoke);
            }}
          />
        }
      />
      <FormControlLabel
        label='Duplicate Title Text'
        control={
          <Checkbox
            color='primary'
            checked={data.duplicateTitle || false}
            onChange={(_, checked) => {
              data.duplicateTitle = checked;
              const updatedInvoke = { ...invoke, data };
              setInvoke(updatedInvoke);
              onChange?.(updatedInvoke);
            }}
          />
        }
      />
      <FormControlLabel
        label='Duplicate Description Text'
        control={
          <Checkbox
            color='primary'
            checked={data.duplicateDescription || false}
            onChange={(_, checked) => {
              data.duplicateDescription = checked;
              const updatedInvoke = { ...invoke, data };
              setInvoke(updatedInvoke);
              onChange?.(updatedInvoke);
            }}
          />
        }
      />
    </StyledConfiguratorDiv>
  );
};

const StyledDisplayDiv = styled.div`
  font-size: 13px;
  padding-left: 4px;
  text-indent: -4px;
  code {
    background: #f4f5fa;
    padding: 2px 5px;
    border-radius: 3px;
  }
  b {
    padding: 0 5px 0 0;
  }
`;

const CreateJiraIssueDisplay: React.FC<ServiceDisplayProps> = ({ invoke }) => {
  return (
    <StyledDisplayDiv>
      {invoke.data?.events?.map((event: any, index: number) => (
        <div className='line' key={index}>
          {event.description || event.event}
        </div>
      ))}
    </StyledDisplayDiv>
  );
};

const StyledDialogContent = styled(DialogContent)`
  .MuiTypography-root,
  .MuiAlert-root {
    &:not(:last-child) {
      margin-bottom: 12px;
    }
  }
`;

interface CreateJiraIssuePluginDialogProps {
  open: boolean;
  state: State<TaskContext>;
  data: CreateJiraIssueParams['data'];
  autoSkip?: boolean;
  onCancel?: () => void;
  onSuccess?: () => void;
}

const CreateJiraIssuePluginDialog: React.FC<
  CreateJiraIssuePluginDialogProps
> = props => {
  const { open, data, state, autoSkip, onCancel, onSuccess } = props;
  const stateRef = useRef(state);
  const onSuccessRef = useRef(onSuccess);
  const [issue, setIssue] = useState<JiraIssue>();
  const [result, setResult] = useState<JiraIssueCreateResponse>();
  const { isAvailable } = useJiraConnectAPI();
  const [foundResult, findIssues] = useFindJiraIssues();
  const [createResult, createIssue] = useCreateJiraIssue();
  const {
    createdTaskJiraIssue,
    creatingTaskJiraIssue,
    createError,
    createTaskJiraIssue
  } = useCreateTaskJiraIssue();
  const { projectId, issueTypeId, linkToEntity } = data;
  const createdIssues = foundResult.data;
  const isLoading =
    createResult.isLoading || foundResult.isLoading || creatingTaskJiraIssue;
  const error = createResult.error || foundResult.error || createError;

  useEffect(() => {
    stateRef.current = state;
  }, [state]);
  useEffect(() => {
    onSuccessRef.current = onSuccess;
  }, [onSuccess]);

  useEffect(() => {
    if (!open) {
      setIssue(undefined);
      setResult(undefined);
    }
  }, [open]);

  useEffect(() => {
    if (!isAvailable || !projectId || !issueTypeId) {
      return;
    }
    const { task, taskEntity } = stateRef.current.context;

    const summary =
      data.enableTitleTemplate && data.titleTemplate
        ? data.titleTemplate
            .replace(/\{key\}/g, task.key || '')
            .replace(/\{name\}/g, task.name)
        : task.name;

    createIssue({
      fields: {
        summary,
        description: (taskEntity as any)?.description || task.description,
        project: { id: projectId },
        issuetype: { id: issueTypeId }
      }
    });
  }, [
    projectId,
    issueTypeId,
    isAvailable,
    data.enableTitleTemplate,
    data.titleTemplate,
    createIssue
  ]);

  useEffect(() => {
    setResult(createResult.data);
  }, [createResult.data]);

  useEffect(() => {
    if (result) {
      findIssues({
        title: result.key,
        data: { reconcileIssues: [Number(result.id)] }
      });
    }
  }, [result, findIssues]);

  useEffect(() => {
    const issue = createdIssues?.data[0];
    issue && setIssue(issue);
  }, [createdIssues]);

  useEffect(() => {
    if (!issue) {
      return;
    }
    if (!linkToEntity) {
      onSuccessRef.current?.();
      return;
    }
    // link issue
    const taskId = stateRef.current.context.task.id!;
    createTaskJiraIssue({
      taskId,
      key: issue.key,
      summary: issue.fields.summary,
      status: issue.fields.status.name,
      issue: issue
    });
  }, [linkToEntity, issue, createTaskJiraIssue]);

  // auot skip if not within jira instance
  useEffect(() => {
    if (!isAvailable && autoSkip) {
      onSuccessRef.current?.();
    }
  }, [autoSkip, isAvailable]);

  useEffect(() => {
    if (createdTaskJiraIssue) {
      onSuccessRef.current?.();
    }
  }, [createdTaskJiraIssue]);

  return (
    <Dialog
      open={open}
      onClose={() => {
        // avoid accidentally cancelling create jira request
        if (!isLoading) {
          onCancel?.();
        }
      }}
    >
      {isLoading && <LinearProgress />}
      <DialogTitle>Create Jira Issue</DialogTitle>
      <StyledDialogContent>
        {isLoading && (
          <Typography>
            {createResult.isLoading
              ? 'Creating Jira Issue...'
              : foundResult.isLoading
              ? `Loading created Jira Issue ${result?.key || '?'}...`
              : creatingTaskJiraIssue
              ? `Linking Jira Issue ${result?.key || '?'}...`
              : 'Loading...'}
          </Typography>
        )}
        {!isAvailable && (
          <Alert severity='warning'>
            <AlertTitle>JIRA Connect API is not available</AlertTitle>
            Unable to run action and create Jira issue. You can choose to skip
            this action, or open SierraQMS within JIRA and try again to properly
            run this action.
          </Alert>
        )}
        {error && <Alert severity='error'>{error.message}</Alert>}
      </StyledDialogContent>
      <DialogActions>
        <Button disabled={isLoading} onClick={onCancel}>
          Cancel
        </Button>
        {!isAvailable && (
          <Button
            color='primary'
            variant='outlined'
            disabled={isLoading}
            onClick={() => onSuccess?.()}
          >
            Skip Action and Proceed
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export const CreateJiraIssuePlugin: ServicePlugin<CreateJiraIssueParams> = {
  src: 'createJiraIssue',
  name: 'Create Jira Issue',
  configurator: CreateJiraIssueConfigurator,
  display: CreateJiraIssueDisplay,
  validate: (condition: Partial<WorkflowInvoke>) => {
    const data = condition.data as CreateJiraIssueParams['data'];
    return !!(data && data.projectId && data.issueTypeId);
  },
  service: (
    state: State<TaskContext>,
    params: CreateJiraIssueParams[] | CreateJiraIssueParams,
    render
  ) => {
    console.log('createJiraIssue params', state, params);
    if (
      state.isSimulation ||
      state.context.skipServicePlugins?.includes(CreateJiraIssuePlugin.src)
    ) {
      return true;
    }
    const pluginParams = Array.isArray(params) ? params[0] : params;
    return new Promise((resolve, reject) => {
      render?.(
        <CreateJiraIssuePluginDialog
          open
          state={state}
          data={pluginParams.data}
          autoSkip={state.context.autoSkipJiraPlugins?.includes(
            CreateJiraIssuePlugin.src
          )}
          onCancel={() => {
            render?.(undefined);
            reject();
          }}
          onSuccess={() => {
            render?.(undefined);
            resolve(true);
          }}
        />
      );
    });
  }
};
